import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Navigate, Outlet, redirect, useParams } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/shared/api/client";
import { meQuery } from "@/shared/lib/session";
import { AppsSettings, WelcomePage } from "./AppCards";
import { AppShell } from "./AppShell";
import { modules } from "./modules";
import { NotFound } from "./NotFound";
import { RouteError } from "./RouteError";
import { recordPath } from "./records";
import { ReportsHub } from "./ReportsHub";
import { SettingsHome, SettingsLayout } from "./SettingsLayout";
import { QuickActions } from "./QuickActions";
import { SetupChecklist } from "./SetupChecklist";
import { DemoOpenPage } from "./Demo";

const { accounting, appointments, auth, crm, dashboard, directory, helpdesk, hr, inventory, invoicing, payroll, pos, projects, realestate, requests, settings } = modules;

type Page = (() => React.ReactNode) | ReturnType<typeof lazyRouteComponent>;

// Every module loads on demand: its chunk is fetched on the first visit to one of its pages.
const from = <T extends Record<string, unknown>>(pages: () => Promise<T>) => (name: keyof T & string): Page => lazyRouteComponent(pages, name);
const authPage = from(auth.pages);
const dashboardPage = from(dashboard.pages);
const directoryPage = from(directory.pages);
const crmPage = from(crm.pages);
const invoicingPage = from(invoicing.pages);
const projectsPage = from(projects.pages);
const requestsPage = from(requests.pages);
const hrPage = from(hr.pages);
const payrollPage = from(payroll.pages);
const accountingPage = from(accounting.pages);
const inventoryPage = from(inventory.pages);
const helpdeskPage = from(helpdesk.pages);
const posPage = from(pos.pages);
const appointmentsPage = from(appointments.pages);
const realestatePage = from(realestate.pages);
const settingsPage = from(settings.pages);

// The dashboard, with the "start here" list of the apps that are on above it.
const Dashboard = lazyRouteComponent(async () => {
  const [{ DashboardPage }, { MyTargets }] = await Promise.all([dashboard.pages(), crm.pages()]);
  return { default: () => <DashboardPage quick={<QuickActions />}><SetupChecklist /><MyTargets /></DashboardPage> };
});

// The self-service portal is HR's page with payroll's payslips and bonuses, and the cash custody one holds, inside it.
const MePortal = lazyRouteComponent(async () => {
  const [{ MePage }, { MyPayslips, MyBonuses }, { MyCustody }] = await Promise.all([hr.pages(), payroll.pages(), invoicing.pages()]);
  return { default: () => <MePage><MyPayslips /><MyBonuses /><MyCustody /></MePage> };
});

const root = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: Outlet, notFoundComponent: NotFound, errorComponent: RouteError });

const page = <P extends string>(path: P, component: Page) => createRoute({ getParentRoute: () => root, path, component });

/** The signed-in user's profile; no session sends them to sign in, then back. */
async function signedIn(queryClient: QueryClient, href: string) {
  try {
    return await queryClient.ensureQueryData(meQuery);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
      throw redirect({ to: "/login", search: { next: href } as never });
    }
    throw e;
  }
}

// First sign-in: whoever may choose the apps picks what the workspace starts with.
const welcome = createRoute({
  getParentRoute: () => root,
  path: "/welcome",
  component: WelcomePage,
  beforeLoad: async ({ context, location }) => {
    if ((await signedIn(context.queryClient, location.href)).tenant.apps_chosen) throw redirect({ to: "/" });
  },
});

// Going in to the demo company as a role: signed in, whether or not the apps are chosen yet.
const demoOpen = createRoute({
  getParentRoute: () => root,
  path: "/demo",
  component: DemoOpenPage,
  beforeLoad: async ({ context, location }) => {
    await signedIn(context.queryClient, location.href);
  },
});

// Signed-in area: loads /me first; 401 sends the user to /login and back afterwards.
const app = createRoute({
  getParentRoute: () => root,
  id: "app",
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    const me = await signedIn(context.queryClient, location.href);
    if (!me.tenant.apps_chosen && me.permissions["core.apps.manage"]) throw redirect({ to: "/welcome" });
  },
});

const a = <P extends string>(path: P, component: Page) => createRoute({ getParentRoute: () => app, path, component });

function RecordRedirect() {
  const { entity, id } = useParams({ strict: false }) as { entity: string; id: string };
  return <Navigate to={recordPath(entity, id)} replace />;
}

const settingsArea = createRoute({ getParentRoute: () => app, path: "/settings", component: SettingsLayout });
const s = <P extends string>(path: P, component: Page) => createRoute({ getParentRoute: () => settingsArea, path, component });

const routeTree = root.addChildren([
  page("/login", authPage("LoginPage")),
  page("/forgot-password", authPage("ForgotPasswordPage")),
  page("/reset-password", authPage("ResetPasswordPage")),
  page("/accept-invite", authPage("AcceptInvitePage")),
  page("/auth/handoff", authPage("HandoffPage")),
  page("/verify-email", authPage("VerifyEmailPage")),
  welcome,
  demoOpen,
  app.addChildren([
    a("/", Dashboard),
    a("/organizations", directoryPage("OrganizationsPage")),
    a("/organizations/duplicates", directoryPage("OrganizationDuplicatesPage")),
    a("/organizations/$id", directoryPage("OrganizationPage")),
    a("/contacts", directoryPage("ContactsPage")),
    a("/contacts/duplicates", directoryPage("ContactDuplicatesPage")),
    a("/contacts/$id", directoryPage("ContactPage")),
    a("/products", directoryPage("ProductsPage")),
    a("/products/labels", directoryPage("LabelsPage")),
    a("/products/groups/$id", directoryPage("ProductGroupPage")),
    a("/products/$id", directoryPage("ProductPage")),
    a("/crm/leads", crmPage("LeadsPage")),
    a("/crm/leads/$id", crmPage("LeadPage")),
    a("/crm/find-leads", crmPage("FindLeadsPage")),
    a("/crm/follow-ups", crmPage("FollowUpsPage")),
    a("/crm/commissions", crmPage("CommissionsPage")),
    a("/crm/sequences", crmPage("SequencesPage")),
    a("/crm/targets", crmPage("TargetsPage")),
    a("/crm/visits", crmPage("VisitsPage")),
    a("/crm/reports", crmPage("SalesReportsPage")),
    a("/crm/board", crmPage("BoardPage")),
    a("/crm/deals", crmPage("DealsPage")),
    a("/crm/deals/$id", crmPage("DealPage")),
    a("/crm/quotes", crmPage("QuotesPage")),
    a("/crm/quotes/new", crmPage("QuoteEditPage")),
    a("/crm/quotes/$id", crmPage("QuotePage")),
    a("/crm/quotes/$id/edit", crmPage("QuoteEditPage")),
    a("/invoicing/invoices", invoicingPage("InvoicesPage")),
    a("/invoicing/invoices/new", invoicingPage("InvoiceEditPage")),
    a("/invoicing/invoices/$id", invoicingPage("InvoicePage")),
    a("/invoicing/invoices/$id/edit", invoicingPage("InvoiceEditPage")),
    a("/invoicing/payments", invoicingPage("PaymentsPage")),
    a("/invoicing/expenses", invoicingPage("ExpensesPage")),
    a("/invoicing/cheques", invoicingPage("ChequesPage")),
    a("/invoicing/treasury", invoicingPage("TreasuryPage")),
    a("/invoicing/treasury/$id", invoicingPage("MoneyAccountPage")),
    a("/invoicing/customers/$id/statement", invoicingPage("StatementPage")),
    a("/invoicing/price-lists", invoicingPage("PriceListsPage")),
    a("/invoicing/price-lists/$id", invoicingPage("PriceListPage")),
    a("/reports", ReportsHub),
    a("/reports/summary", dashboardPage("ReportsPage")),
    a("/reports/custom", dashboardPage("CustomReportsPage")),
    a("/reports/custom/new", dashboardPage("ReportBuilderPage")),
    a("/reports/custom/$id", dashboardPage("ReportBuilderPage")),
    a("/taxes", dashboardPage("TaxesPage")),
    a("/projects", projectsPage("ProjectsPage")),
    a("/projects/$id", projectsPage("ProjectPage")),
    a("/tasks", projectsPage("MyTasksPage")),
    a("/timesheet", projectsPage("TimesheetPage")),
    a("/approvals", projectsPage("ApprovalsPage")),
    a("/me", MePortal),
    a("/hr/employees", hrPage("EmployeesPage")),
    a("/hr/employees/$id", hrPage("EmployeePage")),
    a("/hr/attendance", hrPage("AttendancePage")),
    a("/hr/leaves", hrPage("LeavesPage")),
    a("/hr/custody", hrPage("CustodyPage")),
    a("/hr/org-chart", hrPage("OrgChartPage")),
    a("/hr/checklists", hrPage("ChecklistsPage")),
    a("/hr/violations", hrPage("ViolationsPage")),
    a("/hr/insurance", hrPage("InsurancePage")),
    a("/hr/insurance/$id", hrPage("InsurancePolicyPage")),
    a("/hr/training", hrPage("TrainingPage")),
    a("/hr/training/$id", hrPage("TrainingDetailPage")),
    a("/hr/announcements", hrPage("AnnouncementsPage")),
    a("/hr/reports", hrPage("HrReportsPage")),
    a("/hr/recruitment", hrPage("RecruitmentPage")),
    a("/hr/recruitment/$id", hrPage("OpeningPage")),
    a("/hr/reviews", hrPage("ReviewCyclesPage")),
    a("/hr/reviews/$id", hrPage("ReviewCyclePage")),
    a("/hr/performance", hrPage("PerformancePage")),
    a("/requests", requestsPage("RequestsPage")),
    a("/requests/$id", requestsPage("RequestPage")),
    a("/payroll", payrollPage("PayrollRunsPage")),
    a("/payroll/runs/$id", payrollPage("PayrollRunPage")),
    a("/payroll/advances", payrollPage("AdvancesPage")),
    a("/payroll/end-of-service", payrollPage("EndOfServicePage")),
    a("/payroll/end-of-service/$id", payrollPage("SettlementPage")),
    a("/payroll/bonuses", payrollPage("BonusProgramsPage")),
    a("/payroll/bonuses/new", payrollPage("NewBonusProgramPage")),
    a("/payroll/bonuses/$id", payrollPage("BonusProgramPage")),
    a("/accounting/journal", accountingPage("JournalPage")),
    a("/accounting/accounts", accountingPage("AccountsPage")),
    a("/accounting/reports", accountingPage("AccReportsPage")),
    a("/accounting/bank", accountingPage("BankPage")),
    a("/accounting/assets", accountingPage("AssetsPage")),
    a("/accounting/budgets", accountingPage("BudgetsPage")),
    a("/accounting/periodic", accountingPage("AccPeriodicPage")),
    a("/inventory", inventoryPage("StockPage")),
    a("/inventory/purchase-orders", inventoryPage("PurchaseOrdersPage")),
    a("/inventory/purchase-orders/new", inventoryPage("POEditPage")),
    a("/inventory/purchase-orders/$id", inventoryPage("POPage")),
    a("/inventory/purchase-orders/$id/edit", inventoryPage("POEditPage")),
    a("/inventory/rfqs", inventoryPage("RFQsPage")),
    a("/inventory/rfqs/new", inventoryPage("RFQEditPage")),
    a("/inventory/rfqs/$id", inventoryPage("RFQPage")),
    a("/inventory/rfqs/$id/edit", inventoryPage("RFQEditPage")),
    a("/inventory/bills", inventoryPage("BillsPage")),
    a("/inventory/cheques", inventoryPage("SupplierChequesPage")),
    a("/inventory/suppliers/$id/statement", inventoryPage("SupplierStatementPage")),
    a("/inventory/sales-orders", inventoryPage("SalesOrdersPage")),
    a("/inventory/sales-orders/new", inventoryPage("SalesOrderEditPage")),
    a("/inventory/sales-orders/$id", inventoryPage("SalesOrderPage")),
    a("/inventory/sales-orders/$id/edit", inventoryPage("SalesOrderEditPage")),
    a("/inventory/deliveries", inventoryPage("DeliveriesPage")),
    a("/inventory/transfers", inventoryPage("TransfersPage")),
    a("/inventory/counts", inventoryPage("CountsPage")),
    a("/inventory/manufacturing", inventoryPage("ManufacturingPage")),
    a("/inventory/reports", inventoryPage("InventoryReportsPage")),
    a("/helpdesk", helpdeskPage("InboxPage")),
    a("/helpdesk/tickets", helpdeskPage("TicketsPage")),
    a("/pos", posPage("SellPage")),
    a("/pos/receipts", posPage("ReceiptsPage")),
    a("/pos/shifts", posPage("ShiftsPage")),
    a("/appointments", appointmentsPage("CalendarPage")),
    a("/appointments/list", appointmentsPage("AppointmentsListPage")),
    a("/realestate", realestatePage("UnitsPage")),
    a("/realestate/sales", realestatePage("SalesPage")),
    a("/realestate/collections", realestatePage("CollectionsPage")),
    a("/r/$entity/$id", RecordRedirect),
    settingsArea.addChildren([
      s("/", SettingsHome),
      s("/profile", settingsPage("ProfileSettings")),
      s("/security", settingsPage("SecuritySettings")),
      s("/apps", AppsSettings),
      s("/company", settingsPage("CompanySettings")),
      s("/mail", settingsPage("MailSettings")),
      s("/document-design", settingsPage("DocumentDesignSettings")),
      s("/brand", settingsPage("BrandSettings")),
      s("/opening-balances", settingsPage("OpeningBalancesSettings")),
      s("/import", settingsPage("ImportSettings")),
      s("/subscription", settingsPage("SubscriptionSettings")),
      s("/privacy", settingsPage("PersonalDataSettings")),
      s("/users", settingsPage("UsersSettings")),
      s("/roles", settingsPage("RolesSettings")),
      s("/teams", settingsPage("TeamsSettings")),
      s("/api-keys", settingsPage("APIKeysSettings")),
      s("/webhooks", settingsPage("WebhooksSettings")),
      s("/branches", settingsPage("BranchesSettings")),
      s("/custom-fields", settingsPage("CustomFieldsSettings")),
      s("/automations", settingsPage("AutomationsSettings")),
      s("/taxes", settingsPage("TaxesSettings")),
      s("/pipelines", crmPage("PipelinesSettings")),
      s("/invoicing", invoicingPage("InvoicingSettings")),
      s("/shops", invoicingPage("ShopsSettings")),
      s("/hr", hrPage("HrSettings")),
      s("/requests", requestsPage("RequestTypesSettings")),
      s("/payroll", payrollPage("PayrollSettings")),
      s("/accounting", accountingPage("AccountingSettings")),
      s("/inventory", inventoryPage("InventorySettings")),
      s("/helpdesk", helpdeskPage("HelpdeskSettings")),
      s("/pos", posPage("PosSettings")),
      s("/appointments", appointmentsPage("AppointmentsSettings")),
    ]),
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", defaultPreloadStaleTime: 0, scrollRestoration: true });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
